import { toLocaleTimeStringInTz, toLocaleDateStringInTz } from './dateFormatters';

export interface ParsedLinkedInMessage {
  timestamp: string | null;
  formattedDate: string;
  sender: 'user' | 'assistant';
  body: string;
  dateGroup: string;
  messageType?: string;
  audioUrl?: string | null;
}

const BRACKET_RE = /^\[(\d{4}-\d{2}-\d{2}\s\d{2}:\d{2})\]\s*(user|assistant):$/;
const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
const ROLE_RE = /^(user|assistant):$/;

function isHeaderLine(line: string): boolean {
  return BRACKET_RE.test(line) || ISO_RE.test(line) || ROLE_RE.test(line);
}

function toISOTimestamp(raw: string): string {
  return raw.includes('T') ? raw : raw.replace(' ', 'T') + ':00';
}

export const parseLinkedInThread = (threadText: string, tz?: string): ParsedLinkedInMessage[] => {
  if (!threadText || threadText.trim() === '') {
    return [];
  }

  const messages: ParsedLinkedInMessage[] = [];
  const normalizedText = threadText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalizedText.split('\n');
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    if (!line) {
      i++;
      continue;
    }

    let timestamp: string | null = null;
    let sender: 'user' | 'assistant' | null = null;

    const bracketMatch = line.match(BRACKET_RE);
    if (bracketMatch) {
      timestamp = toISOTimestamp(bracketMatch[1]);
      sender = bracketMatch[2] as 'user' | 'assistant';
      i++;
    } else if (ISO_RE.test(line)) {
      timestamp = line;
      i++;
      if (i >= lines.length) break;
      const nextLine = lines[i]?.trim();
      const roleMatch = nextLine?.match(ROLE_RE);
      if (roleMatch) {
        sender = roleMatch[1] as 'user' | 'assistant';
        i++;
      }
    } else {
      const roleMatch = line.match(ROLE_RE);
      if (roleMatch) {
        sender = roleMatch[1] as 'user' | 'assistant';
        i++;
      }
    }

    if (!sender) {
      i++;
      continue;
    }

    let body = '';
    while (i < lines.length) {
      const nextLine = lines[i].trim();
      if (isHeaderLine(nextLine)) break;
      body += lines[i] + '\n';
      i++;
    }

    const trimmedBody = body.trim();
    if (trimmedBody) {
      messages.push({
        timestamp,
        formattedDate: timestamp ? formatMessageTime(timestamp, tz) : '',
        sender,
        body: trimmedBody,
        dateGroup: timestamp ? getDateGroup(timestamp, tz) : '',
      });
    }
  }

  return messages;
};

const formatMessageTime = (timestamp: string, tz?: string): string => {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    const timeString = toLocaleTimeStringInTz(date, tz, 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    if (diffInDays === 0) return `Today at ${timeString}`;
    if (diffInDays === 1) return `Yesterday at ${timeString}`;
    if (diffInDays < 7) {
      const dayName = toLocaleDateStringInTz(date, tz, 'en-US', { weekday: 'long' });
      return `${dayName} at ${timeString}`;
    }

    const dateString = toLocaleDateStringInTz(date, tz, 'en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
    return `${dateString} at ${timeString}`;
  } catch {
    return '';
  }
};

const getDateGroup = (timestamp: string, tz?: string): string => {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';

    return toLocaleDateStringInTz(date, tz, 'en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  } catch {
    return '';
  }
};
